/* utils/chordParse.js
   文字列のコードシンボルをパースして { root, quality, tensions, bass } を返す */

const NOTE_TO_PC = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, Fb: 4, 'E#': 5,
  F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11, Cb: 11, 'B#': 0,
};

function normalizeRoot(root, accidental = '') {
  return (root.toUpperCase() + accidental);
}

function parseQuality(q) {
  if (!q) return 'maj';
  const s = String(q);
  if (/^(maj7|Δ7)$/i.test(s) || s === 'M7') return 'maj7';
  if (/^M$/.test(s)) return 'maj';
  if (/^(maj|Δ)$/i.test(s)) return 'maj';
  if (/^(m7b5|ø7|ø)$/i.test(s)) return 'm7b5';
  if (/^(m7)$/i.test(s)) return 'm7';
  if (/^(m|min)$/i.test(s)) return 'm';
  if (/^m(add)?(6|9|11|13|69)$/i.test(s)) return 'm';
  if (/^(dim7|°7)$/i.test(s)) return 'dim7';
  if (/^(dim|°)$/i.test(s)) return 'dim';
  if (/^(aug7|\+7)$/i.test(s)) return 'aug7';
  if (/^(aug|\+|#5)$/i.test(s)) return 'aug';
  if (/^7$/i.test(s)) return '7';
  if (/^sus(2|4)$/i.test(s)) return s.toLowerCase();
  return s.toLowerCase();
}

function parseTensions(t) {
  if (!t) return [];
  const parts = t.split(/[ ,]+/).map((x) => x.trim()).filter(Boolean);
  const allow = new Set(['b5', '#5', 'b9', '#9', '9', '11', '#11', '13', 'b13', '6']);
  const out = [];
  for (const p of parts) {
    if (!allow.has(p)) return null;
    out.push(p);
  }
  return Array.from(new Set(out));
}

function parseInlineTensions(text = '') {
  if (!text) return [];
  const sanitized = text.replace(/add/gi, '');
  const tokens = [];
  const re = /([#b]?)(5|6|9|11|13)/gi;
  let match;
  while ((match = re.exec(sanitized)) !== null) {
    const accidental = match[1] || '';
    const number = match[2];
    let token = (accidental + number).toLowerCase();
    if (token === 'b6') token = 'b13';
    tokens.push(token);
  }
  return Array.from(new Set(tokens));
}

function normalizeQualityDescriptor(token = '', collector = []) {
  const lower = token.toLowerCase();
  switch (lower) {
    case 'mmaj7':
    case 'mm7':
      return 'mmaj7';
    case '7sus4': return '7sus4';
    case '7sus2': return '7sus2';
    case '5': return '5';
    case 'maj13':
      collector.push('9', '13');
      return 'maj7';
    case 'maj9':
      collector.push('9');
      return 'maj7';
    case 'm13':
      collector.push('9', '11', '13');
      return 'm7';
    case 'm11':
      collector.push('9', '11');
      return 'm7';
    case 'm9':
      collector.push('9');
      return 'm7';
    case 'min7':
      return 'm7';
    case 'min':
      return 'm';
    case '9':
      collector.push('9');
      return '7';
    case '11':
      collector.push('9', '11');
      return '7';
    case '13':
      collector.push('9', '13');
      return '7';
    case '6':
      collector.push('6');
      return 'maj';
    default:
      return parseQuality(token);
  }
}

function parseDescriptor(raw = '') {
  const descriptor = raw.trim();
  if (!descriptor) return { quality: 'maj', inline: [] };
  const normalizedDescriptor = descriptor.replace(/^mM7/, 'mmaj7').replace(/^Δ/, 'maj').replace(/^M(?=\d|$)/, 'maj').replace(/sus$/i, 'sus4');
  const candidates = [
    'maj13', 'maj9', 'maj7', 'maj',
    'mmaj7', 'm13', 'm11', 'm9', 'm7b5', 'm7', 'min7', 'min', 'm',
    'dim7', 'dim', '°7', '°',
    'aug7', 'aug',
    '+7', '+', '#5',
    '7sus4', '7sus2', 'sus4', 'sus2',
    'ø7', 'ø',
    '13', '11', '9', '7', '6', '5',
  ];
  const lower = normalizedDescriptor.replace(/^sus([24])7$/i, '7sus$1').toLowerCase();
  let matched = '';
  for (const candidate of candidates) {
    if (lower.startsWith(candidate)) {
      matched = candidate;
      break;
    }
  }
  const inlineFromQuality = [];
  const quality = normalizeQualityDescriptor(matched, inlineFromQuality);
  const rest = lower.slice(matched.length);
  if (rest.replace(/(?:add)?[#b]?(?:13|11|9|6|5)/gi, '') !== '') return null;
  const inline = Array.from(
    new Set([...inlineFromQuality, ...parseInlineTensions(rest)])
  );
  return { quality, inline };
}

export function parseChordSymbol(sym) {
  if (!sym || typeof sym !== 'string') return null;
  const s = sym.trim().replace(/♭/g, 'b').replace(/♯/g, '#').replace(/6\/9(?=$|\/[A-Ga-g])/, '69').replace(/m\((?:maj7|M7|Δ7)\)/, 'mmaj7');
  const re = /^([A-Ga-g])([b#]?)([^()/]*)?(?:\(([^)]*)\))?(?:\/([A-Ga-g][b#]?))?$/;
  const m = s.match(re);
  if (!m) return null;
  const [, r, acc, descriptorRaw, tensionsRaw, bassRaw] = m;
  const root = normalizeRoot(r, acc);
  let descriptor = descriptorRaw || '';
  const parsedDescriptor = parseDescriptor(descriptor);
  if (!parsedDescriptor) return null;
  let { quality, inline } = parsedDescriptor;
  const explicitTensions = parseTensions(tensionsRaw);
  if (!explicitTensions) return null;
  const tensions = Array.from(new Set([...inline, ...explicitTensions]));
  let bass;
  if (bassRaw) {
    const bassMatch = bassRaw.match(/^([A-Ga-g])([b#]?)$/);
    if (bassMatch) {
      bass = normalizeRoot(bassMatch[1], bassMatch[2]);
    }
  }
  return { root, quality, tensions, bass };
}

export function rootToPc(root) {
  return NOTE_TO_PC[root] ?? null;
}
